import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import ConfirmModal from "../components/ConfirmModal";
import "./ManageRoles.css";

const roleIcons = {
  owner: "⭐",
  editor: "✏️",
  viewer: "👁️"
};

const ManageRoles = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [search, setSearch] = useState("");

  /* Confirm Modal State */
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    message: "",
    action: null,
    payload: null,
  });

  const updateRole = async (userId, role) => {
    try {
      await api.post(`/books/${bookId}/roles/update`, { userId, role });
      fetchBook();
    } catch (err) {
      alert("Failed to update role");
    }
  };

  const fetchBook = async () => {
    try {
      const res = await api.get(`/books/${bookId}`);
      setBook(res.data.book);
    } catch (err) {
      alert("Error loading book");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBook();
  }, [bookId]);

  if (loading) return <div className="centered">Loading...</div>;
  if (!book) return <div className="centered">Book not found</div>;

  const currentUserId = user?._id;
  const ownerId = book.owner.toString();

  const hasOwnerRights =
    ownerId === currentUserId ||
    book.members.some((m) => m.user?._id === currentUserId && m.role === "owner");

  /* -----------------------------
       CONFIRMATION HANDLERS 
  ------------------------------ */

  const askRemoveMember = (userId) => {
    setConfirmModal({
      open: true,
      message: "Remove this member permanently?",
      action: "remove",
      payload: userId,
    });
  };

  const askCancelInvite = (mobile) => {
    setConfirmModal({
      open: true,
      message: "Cancel this invitation?",
      action: "cancelInvite",
      payload: mobile,
    });
  };

  const handleConfirm = async () => {
    const { action, payload } = confirmModal;

    try {
      if (action === "remove") {
        await api.post(`/books/${bookId}/roles/remove`, { userId: payload });
      } else if (action === "cancelInvite") {
        await api.post(`/books/${bookId}/invites/cancel`, { mobile: payload });
      }
      fetchBook();
    } catch (err) {
      alert("Failed to complete action");
    }

    setConfirmModal({ open: false });
  };

  const closeConfirm = () =>
    setConfirmModal({ open: false, action: null, payload: null });

  /* SEARCH FILTER */
  const filteredMembers = book.members.filter((m) =>
    `${m.user?.name} ${m.user?.mobile}`.toLowerCase().includes(search.toLowerCase())
  );

  // Share invite handler
  const shareInvite = async (inv) => {
  try {
    const inviteLink = `https://cashify.gradezy.in/books/invite/${inv._id}?fromInvite=true`;

    if (navigator.share) {
      await navigator.share({
        title: `Invite to ${book.name}`,
        text: `You have been invited to join "${book.name}" as a ${inv.role}.`,
        url: inviteLink,
      });
    } else {
      await navigator.clipboard.writeText(inviteLink);
      alert("Invite link copied to clipboard. Share it with the user.");
    }
  } catch (err) {
    console.error("Share failed", err);
    const inviteLink = `https://cashify.gradezy.in/books/invite/${inv._id}?fromInvite=true`;
    window.prompt("Copy invite link:", inviteLink);
  }
};


  return (
    <div className="roles-page">

      {/* HEADER */}
      <div className="roles-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left"></i>
        </button>
        <h2>Manage Roles</h2>
      </div>

      {/* SEARCH BAR */}
      <div className="roles-search">
        <i className="bi bi-search search-icon"></i>
        <input
          type="text"
          placeholder="Search members..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <h3 className="section-title">Members</h3>

      <div className="roles-card">
        {filteredMembers.map((m) => {
          const isOwner = m.user?._id === ownerId;
          const isExpanded = expanded === m._id;

          return (
            <div key={m._id} className="role-item">
              <div className="role-top">

                {/* LEFT SIDE */}
                <div className="role-left">
                  <div className={`avatar role-${m.role}`}>
                    {m.user?.name?.[0]?.toUpperCase() ||
                      m.user?.mobile?.[0]}
                  </div>

                  <div className="info">
                    <strong>{m.user?.name}</strong>
                    <div className="muted">{m.user?.mobile}</div>
                  </div>
                </div>

                {/* RIGHT SIDE */}
                {isOwner ? (
                  <span className="role-badge">⭐ OWNER</span>
                ) : hasOwnerRights ? (
                  <button
                    className="expand-btn"
                    onClick={() => setExpanded(isExpanded ? null : m._id)}
                  >
                    <i className={`bi ${isExpanded ? "bi-chevron-up" : "bi-chevron-down"}`}></i>
                  </button>
                ) : (
                  <span className="muted small">No Access</span>
                )}
              </div>

              {/* DROPDOWN */}
              {isExpanded && (
                <div className="role-dropdown">
                  <select
                    className="role-select"
                    value={m.role}
                    onChange={(e) => updateRole(m.user._id, e.target.value)}
                  >
                    <option value="owner">⭐ OWNER</option>
                    <option value="editor">✏️ EDITOR</option>
                    <option value="viewer">👁️ VIEWER</option>
                  </select>

                  <button
                    className="remove-btn"
                    onClick={() => askRemoveMember(m.user._id)}
                  >
                    <i className="bi bi-x-circle"></i> Remove
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* PENDING INVITES */}
      <h3 className="section-title">Pending Invitations</h3>

      <div className="roles-card">
        {book.invites.length === 0 ? (
          <div className="muted">No pending invites</div>
        ) : (
          book.invites.map((inv) => (
            <div key={inv._id} className="invite-row">
              
              <div className="invite-left">
                <div className="avatar invite-avatar">?</div>
                <div>
                  <strong>{inv.mobile}</strong>
                  <div className="muted">
                    Role: {roleIcons[inv.role]} {inv.role}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8 }}>
                {/* Share button — styled same as remove */}
                <button
                  className="invite-remove"
                  onClick={() => shareInvite(inv)}
                  title="Share invite"
                >
                  <i className="bi bi-share-fill"></i>
                </button>

                {/* Remove/cancel invite */}
                <button
                  className="invite-remove"
                  onClick={() => askCancelInvite(inv.mobile)}
                  title="Cancel invitation"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* MODAL */}
      <ConfirmModal
        open={confirmModal.open}
        title="Confirm Action"
        message={confirmModal.message}
        onCancel={closeConfirm}
        onConfirm={handleConfirm}
      />
    </div>
  );
};

export default ManageRoles;
